import { useCallback, useRef, useState } from "react";
import {
  DEFAULT_SIZE_CAP_MB,
  DriveSkippedError,
  GOOGLE_DRIVE_SCOPE,
  GOOGLE_OAUTH_READY,
  GoogleAuthError,
  MAX_FOLDER_DEPTH,
  classifyDriveFiles,
  countByExt,
  downloadDriveFileAsBlob,
  ensureGapiPickerLoaded,
  ensureGsiLoaded,
  formatBytesPretty,
  listFolderChildren,
  listFolderRecursively,
  openDrivePicker,
  refreshGoogleAccessToken,
  requestGoogleAccessToken,
  runWithConcurrency,
  type ClassifiedDriveFile,
  type DriveClassification,
  type DriveFile,
  type PickerDoc,
} from "../api";

import styles from "./DrivePickerPanel.module.css";

interface Props {
  // Hands picked + downloaded files to the parent's upload state.
  // Each is a real `File` ready for the existing
  // /uploads/initiate -> S3 PUT -> /uploads/complete pipeline.
  onAddFiles: (files: File[]) => void;
  // Optional: panel disables itself while the parent is mid-submit.
  disabled?: boolean;
  // Optional: parent can adopt the picked Drive folder's name as
  // the project_id when the operator hasn't typed one. Matches the
  // legacy `onProjectIdSuggested` callback.
  onProjectIdSuggested?: (name: string) => void;
}

interface DrivePreviewState extends DriveClassification {
  allDocs: DriveFile[];
  sizeCapMB: number;
  suggestedProjectId: string | null;
}

// Pick a Drive folder (or individual files), preview the
// classification by inferKind + size, raise / lower the per-file
// cap, then confirm to recursively download.
//
// OAuth: operator clicks "Pick from Drive" -> panel ensures both
// GSI + gapi.picker scripts loaded -> requests a `drive.readonly`
// token -> opens the Picker -> recursively lists picked folders ->
// shows the preview -> on confirm, downloads concurrently (6-way).
// Token cached in component state for the page lifetime; 401
// recovery same shape as the Gmail panel.
//
// Operator-side OAuth on the operator's own Google account; NOT
// the InRange API auth seam. No User class, no AuthProvider.
export const DrivePickerPanel = ({ onAddFiles, disabled, onProjectIdSuggested }: Props) => {
  const [token, setToken] = useState<string | null>(null);
  const [preview, setPreview] = useState<DrivePreviewState | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number; lastName?: string } | null>(null);
  // AbortController for the current confirm-batch download. Reset
  // flips the signal so workers bail out promptly + the in-flight
  // fetch is cancelled rather than draining to completion.
  const abortRef = useRef<AbortController | null>(null);

  const inflight: boolean = busy || disabled === true;

  // Acquire a Drive token. One consent per page load - we cache
  // in component state. When the ~1h token expires the call sites
  // catch 401, drop the cache, re-acquire, retry once.
  const ensureToken = useCallback(async (): Promise<string> => {
    if (token) return token;
    await ensureGsiLoaded();
    const fresh = await requestGoogleAccessToken([GOOGLE_DRIVE_SCOPE]);
    setToken(fresh);
    return fresh;
  }, [token]);

  // Wraps any Drive call site; on 401 we drop the token, re-acquire,
  // retry once. Uses the module-scoped `refreshGoogleAccessToken`
  // so that six concurrent workers all hitting 401 simultaneously
  // collapse onto a single consent popup rather than racing six
  // popups - the second through sixth await the same in-flight
  // refresh promise.
  const callWith401Retry = useCallback(
    async <T,>(op: (accessToken: string) => Promise<T>): Promise<T> => {
      const accessToken = await ensureToken();
      try {
        return await op(accessToken);
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        if (!/\(401\)/.test(msg)) throw e;
        setToken(null);
        const refreshed = await refreshGoogleAccessToken([GOOGLE_DRIVE_SCOPE]);
        setToken(refreshed);
        return op(refreshed);
      }
    },
    [ensureToken],
  );

  const reset = useCallback(() => {
    // Trip the abort signal so in-flight downloads stop promptly.
    // Workers either haven't started their next item (skip via
    // signal.aborted check) or are mid-fetch (AbortError propagates
    // through `runWithConcurrency.errors`).
    abortRef.current?.abort();
    abortRef.current = null;
    setPreview(null);
    setProgress(null);
    setStatus(null);
    setErr(null);
  }, []);

  const onPickClick = useCallback(async () => {
    if (inflight) return;
    setBusy(true);
    setErr(null);
    setStatus("Loading Google sign-in…");
    setPreview(null);
    try {
      await ensureGsiLoaded();
      await ensureGapiPickerLoaded();
      const accessToken = await ensureToken();
      setStatus("Opening Drive picker…");
      const docs: PickerDoc[] | null = await openDrivePicker(accessToken);
      if (!docs) {
        // CANCEL action - operator closed the picker without
        // picking. Silent abort, no red box.
        setStatus(null);
        return;
      }
      if (docs.length === 0) {
        setStatus("Picker returned no items.");
        return;
      }
      const folders = docs.filter((d) => d.mimeType === "application/vnd.google-apps.folder");
      const direct = docs.filter((d) => d.mimeType !== "application/vnd.google-apps.folder");
      const suggestedProjectId = folders[0]?.name ?? null;

      setStatus("Listing folder contents…");
      const folderFiles: DriveFile[] = [];
      const emptyFolders: { name: string; id: string }[] = [];
      // Bind `callWith401Retry` around the leaf list call so token
      // expiry mid-walk only retries that single page, preserving
      // the depth/visited state for the rest of the tree.
      const listOnePage = (id: string) =>
        callWith401Retry((tok) => listFolderChildren(id, tok));
      for (const folder of folders) {
        setStatus(`Listing files in "${folder.name}"…`);
        const found = await listFolderRecursively(folder.id, listOnePage);
        if (found.length === 0) emptyFolders.push({ name: folder.name, id: folder.id });
        folderFiles.push(...found);
      }
      // Picker returns `Doc` shape (id/name/mimeType); we widen
      // direct files into DriveFile by accepting the missing
      // optional fields.
      const allDocs: DriveFile[] = [
        ...direct.map<DriveFile>((d) => ({ id: d.id, name: d.name, mimeType: d.mimeType })),
        ...folderFiles,
      ];
      if (allDocs.length === 0) {
        setErr(
          emptyFolders.length > 0
            ? `Folder${emptyFolders.length === 1 ? "" : "s"} ${emptyFolders
                .map((f) => `"${f.name}"`)
                .join(", ")} contained no files after recursing ${MAX_FOLDER_DEPTH} levels deep.`
            : "Drive returned 0 files for the picked items.",
        );
        setStatus(null);
        return;
      }
      const classified = classifyDriveFiles(allDocs, DEFAULT_SIZE_CAP_MB);
      setPreview({
        allDocs,
        sizeCapMB: DEFAULT_SIZE_CAP_MB,
        suggestedProjectId,
        ...classified,
      });
      setStatus(null);
    } catch (e) {
      if (e instanceof GoogleAuthError && e.isUserCancelled) {
        setStatus(null);
      } else {
        setErr(e instanceof Error ? e.message : String(e));
        setStatus(null);
      }
    } finally {
      setBusy(false);
    }
  }, [inflight, ensureToken, callWith401Retry]);

  // Slide the per-file size cap. Pure recompute against the
  // already-listed files - no new network calls.
  const onChangeSizeCap = (capMB: number) => {
    if (!preview) return;
    const reclassified = classifyDriveFiles(preview.allDocs, capMB);
    setPreview({ ...preview, sizeCapMB: capMB, ...reclassified });
  };

  const onCancel = () => {
    setPreview(null);
    setStatus(null);
    setErr(null);
  };

  const onConfirm = useCallback(async () => {
    if (!preview) return;
    const { toImport, suggestedProjectId } = preview;
    if (toImport.length === 0) {
      setErr("Nothing to import - every picked file is either too big or unsupported.");
      return;
    }
    setBusy(true);
    setErr(null);
    setProgress({ done: 0, total: toImport.length });
    // Project-ID inheritance fires only after the operator commits;
    // a cancelled preview shouldn't rename the project.
    if (suggestedProjectId) onProjectIdSuggested?.(suggestedProjectId);
    // Fresh AbortController for this batch. Reset trips it.
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const { results, errors, aborted } = await runWithConcurrency<ClassifiedDriveFile, File>(
        toImport,
        (d, signal) => callWith401Retry((tok) => downloadDriveFileAsBlob(d, tok, signal)),
        {
          concurrency: 6,
          signal: controller.signal,
          onItem: ({ done, total, name }) => {
            setProgress({ done, total, lastName: name });
          },
        },
      );
      if (aborted) {
        // Reset was clicked mid-batch. Any files that finished
        // before the abort are still useful - hand them up - but
        // skip the status summary since the panel state was
        // already cleared by `reset`.
        if (results.length > 0) onAddFiles(results);
        return;
      }
      onAddFiles(results);
      const skipped = errors.filter((e) => e.error instanceof DriveSkippedError);
      const hard = errors.filter((e) => !(e.error instanceof DriveSkippedError));
      const summary =
        `Imported ${results.length} of ${toImport.length} file${toImport.length === 1 ? "" : "s"} ` +
        `(${formatBytesPretty(preview.bytes.toImport)})`;
      const skipNote = skipped.length ? ` · ${skipped.length} skipped (Forms/Sites/Maps/oversize export)` : "";
      const failNote = hard.length
        ? ` · ${hard.length} failed (${hard
            .slice(0, 2)
            .map((e) => e.error.message?.slice(0, 80))
            .join("; ")}${hard.length > 2 ? "…" : ""})`
        : "";
      setStatus(summary + skipNote + failNote);
      setPreview(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      // Only clear `abortRef` if we still own it - reset() may
      // already have nulled it.
      if (abortRef.current === controller) abortRef.current = null;
      setBusy(false);
      setProgress(null);
    }
  }, [preview, onAddFiles, onProjectIdSuggested, callWith401Retry]);

  if (!GOOGLE_OAUTH_READY) {
    return (
      <section className={styles.section}>
        <div className={styles.eyebrow}>Pick from Google Drive</div>
        <p className={styles.note}>
          Drive integration is not configured for this deploy. Set{" "}
          <code>GOOGLE_CLIENT_ID</code> + <code>GOOGLE_API_KEY</code> in{" "}
          <code>web/src/api/google-config.ts</code> and add the v2 CloudFront
          origin to the Google Cloud Console allowlist.
        </p>
      </section>
    );
  }

  return (
    <section className={styles.section}>
      <div className={styles.head}>
        <div className={styles.eyebrow}>Pick from Google Drive</div>
        <div className={styles.title}>
          Import a folder or individual files from Drive
        </div>
        <div className={styles.note}>
          Recursive folder walk up to {MAX_FOLDER_DEPTH} levels deep.
          Read-only — the agent only sees files you confirm.
          Files larger than the per-file cap or in formats the agent can't read
          (CAD, video, raw imagery) are excluded automatically.
        </div>
      </div>

      <div className={styles.pickRow}>
        <button
          type="button"
          onClick={() => void onPickClick()}
          disabled={inflight}
          className={styles.pickBtn}
        >
          {busy && !preview && !progress
            ? "Working…"
            : token
              ? "Pick from Drive"
              : "Sign in & pick from Drive"}
        </button>
        {(preview || progress) && (
          <button
            type="button"
            onClick={reset}
            className={styles.cancelBtn}
          >
            Reset
          </button>
        )}
      </div>

      {err && <div className={styles.err}>{err}</div>}
      {status && !err && <div className={styles.status}>{status}</div>}

      {progress && (
        <DriveProgressBar
          progress={progress}
          bytes={preview?.bytes.toImport ?? 0}
        />
      )}

      {preview && !progress && (
        <DriveImportPreview
          preview={preview}
          onChangeSizeCap={onChangeSizeCap}
          onConfirm={() => void onConfirm()}
          onCancel={onCancel}
          busy={inflight}
        />
      )}
    </section>
  );
};

// ---------------------------------------------------------------------- //
// Preview panel
// ---------------------------------------------------------------------- //

interface PreviewProps {
  preview: DrivePreviewState;
  onChangeSizeCap: (capMB: number) => void;
  onConfirm: () => void;
  onCancel: () => void;
  busy: boolean;
}

const DriveImportPreview = ({
  preview,
  onChangeSizeCap,
  onConfirm,
  onCancel,
  busy,
}: PreviewProps) => {
  const { toImport, tooBig, unsupported, bytes, sizeCapMB, suggestedProjectId } = preview;
  const totalFiles = toImport.length + tooBig.length + unsupported.length;
  const totalBytes = bytes.toImport + bytes.tooBig + bytes.unsupported;
  const nothingToImport = toImport.length === 0;

  return (
    <div className={`${styles.preview} ${nothingToImport ? styles.previewEmpty : ""}`}>
      <div className={styles.previewHead}>
        <div className={styles.previewEyebrow}>Drive import preview</div>
        <div className={styles.previewTitle}>
          {totalFiles} file{totalFiles === 1 ? "" : "s"} found
          {totalBytes > 0 ? ` · ${formatBytesPretty(totalBytes)} total` : ""}
          {suggestedProjectId ? ` · folder "${suggestedProjectId}"` : ""}
        </div>
      </div>

      {nothingToImport && (
        <div className={styles.emptyBanner}>
          <strong>Nothing will be imported.</strong>{" "}
          {tooBig.length > 0 && unsupported.length === 0 && (
            <>
              All {tooBig.length} picked file{tooBig.length === 1 ? "" : "s"} exceed
              the {sizeCapMB} MB per-file cap (totals{" "}
              {formatBytesPretty(bytes.tooBig)}). Raise the cap below, or pick
              smaller files.
            </>
          )}
          {tooBig.length === 0 && unsupported.length > 0 && (
            <>
              All {unsupported.length} picked file
              {unsupported.length === 1 ? "" : "s"} are types the agent's
              extractors can't read (CAD, video, archives, etc.). Pick PDFs,
              DOCX, XLSX, CSV, or images instead.
            </>
          )}
          {tooBig.length > 0 && unsupported.length > 0 && (
            <>
              {tooBig.length} file{tooBig.length === 1 ? "" : "s"} exceed the{" "}
              {sizeCapMB} MB cap and {unsupported.length}{" "}
              {unsupported.length === 1 ? "is" : "are"} an unsupported type.
            </>
          )}
        </div>
      )}

      <div className={styles.bucketRow}>
        <DriveBucket
          tone="ok"
          label="Will import"
          count={toImport.length}
          bytes={bytes.toImport}
          breakdown={countByExt(toImport)}
        />
        {tooBig.length > 0 && (
          <DriveBucket
            tone="warn"
            label={`Too large (>${sizeCapMB} MB)`}
            count={tooBig.length}
            bytes={bytes.tooBig}
            breakdown={countByExt(tooBig)}
            note="Lift the cap below to include these."
          />
        )}
        {unsupported.length > 0 && (
          <DriveBucket
            tone="muted"
            label="Skipped (agent can't read)"
            count={unsupported.length}
            bytes={bytes.unsupported}
            breakdown={countByExt(unsupported)}
            note="CAD, video, raw imagery, archives — agent's extractors only handle PDF / DOCX / XLSX / CSV / images."
          />
        )}
      </div>

      <div className={styles.controls}>
        <label className={styles.capLabel}>
          Per-file size cap:
          <select
            value={sizeCapMB}
            onChange={(e) => onChangeSizeCap(Number(e.target.value))}
            className={styles.capSelect}
            disabled={busy}
          >
            {[5, 10, 15, 20, 25].map((mb) => (
              <option key={mb} value={mb}>
                {mb} MB
              </option>
            ))}
          </select>
        </label>
        <div className={styles.previewActions}>
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className={styles.cancelBtn}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy || toImport.length === 0}
            className={styles.confirmBtn}
          >
            Import {toImport.length} file{toImport.length === 1 ? "" : "s"} (
            {formatBytesPretty(bytes.toImport)})
          </button>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------- //
// Per-bucket box
// ---------------------------------------------------------------------- //

interface BucketProps {
  tone: "ok" | "warn" | "muted";
  label: string;
  count: number;
  bytes: number;
  breakdown: [string, number][];
  note?: string;
}

const DriveBucket = ({ tone, label, count, bytes, breakdown, note }: BucketProps) => (
  <div className={`${styles.bucket} ${styles[`bucket_${tone}`]}`}>
    <div className={styles.bucketLabel}>{label}</div>
    <div className={styles.bucketCount}>
      {count}
      <span className={styles.bucketBytes}> · {formatBytesPretty(bytes)}</span>
    </div>
    {breakdown.length > 0 && (
      <div className={styles.bucketBreakdown}>
        {breakdown
          .slice(0, 6)
          .map(([k, n]) => `${k} ×${n}`)
          .join(", ")}
        {breakdown.length > 6 ? "…" : ""}
      </div>
    )}
    {note && <div className={styles.bucketNote}>{note}</div>}
  </div>
);

// ---------------------------------------------------------------------- //
// Progress bar
// ---------------------------------------------------------------------- //

interface ProgressProps {
  progress: { done: number; total: number; lastName?: string };
  bytes: number;
}

const DriveProgressBar = ({ progress, bytes }: ProgressProps) => {
  const pct = progress.total > 0 ? (progress.done / progress.total) * 100 : 0;
  return (
    <div className={styles.progress}>
      <div className={styles.progressText}>
        Downloading {progress.done} / {progress.total}
        {bytes ? ` · target ${formatBytesPretty(bytes)}` : ""}
        {progress.lastName ? ` · last: ${progress.lastName}` : ""}
      </div>
      <div className={styles.progressTrack}>
        <div className={styles.progressFill} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};
